import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import {
    Dialog, Box, Typography, IconButton, TextField, Chip,
    CircularProgress, InputAdornment, Button, Divider, useMediaQuery, useTheme,
} from '@mui/material';
import {
    Close as CloseIcon,
    Search as SearchIcon,
    MenuBook as MenuBookIcon,
    ArrowBackIosNew as BackIcon,
    Person as AuthorIcon,
    VerifiedUser as ApprovedIcon,
    EventRepeat as ReviewIcon,
    Layers as VersionIcon,
    ChecklistRtl as StepsIcon,
    CloudOff as CloudOffIcon,
    Refresh as RefreshIcon,
} from '@mui/icons-material';

import { BRAND, FONT_STACK, GRADIENT_NAVY } from '../../brand';
import { loadAllSOPDocumentsdb, loadSOPDocumentByIddb } from '../BaseURL';

// ============================================================================
//  SopLibraryDialog — browse the published Policy & SOP library
// ============================================================================
//
//  A master–detail browser in one dialog: the index on the left, the selected
//  document's full procedure on the right. The earlier version used two
//  stacked dialogs (grid → steps), which meant closing one to reach another
//  document. Staff comparing two procedures want to move between them in one
//  place, so the list stays on screen while the detail changes.
//
//  Reachable BEFORE sign-in, so it is strictly read-only and shows nothing but
//  the published policy library — no patient data, no edit path.
//
//  Self-contained: it owns its own fetching and caching, so the login screen
//  only has to say whether it is open.

const TYPE_COLORS = { SOP: BRAND.green, Policy: BRAND.navyLight };

const STATUS_COLORS = {
    Active:         BRAND.green,
    Draft:          BRAND.warning,
    'Under Review': '#EA580C',
    Archived:       BRAND.inkFaint,
};

function normaliseDoc(row) {
    return {
        ...row,
        id:            Number(row.id),
        tags:          row.tags ? row.tags.split(',').map(t => t.trim()).filter(Boolean) : [],
        steps:         row.steps || [],
        review_date:   row.review_date ? row.review_date.split('T')[0] : '',
        categoryLabel: row.category_label || row.category,
    };
}

// ─── One row in the index ───────────────────────────────────────────────────
function IndexRow({ doc, selected, onSelect }) {
    const color = TYPE_COLORS[doc.type] || BRAND.navy;
    return (
        <Box
            onClick={() => onSelect(doc.id)}
            sx={{
                display: 'flex', gap: 1.25, alignItems: 'stretch', cursor: 'pointer',
                px: 1.5, py: 1.25, borderRadius: '10px',
                background: selected ? '#fff' : 'transparent',
                border: `1px solid ${selected ? color : 'transparent'}`,
                boxShadow: selected ? '0 3px 12px rgba(15,31,61,.09)' : 'none',
                transition: 'background .14s ease, border-color .14s ease',
                '&:hover': { background: selected ? '#fff' : 'rgba(255,255,255,.7)' },
            }}
        >
            <Box sx={{ width: 3, borderRadius: 2, background: color, flexShrink: 0 }} />
            <Box sx={{ minWidth: 0, flex: 1 }}>
                <Typography
                    sx={{
                        fontSize: 12.5, fontWeight: 700, lineHeight: 1.4,
                        color: selected ? BRAND.ink : BRAND.inkMuted,
                        display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden',
                    }}
                >
                    {doc.title}
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: .75, mt: .5 }}>
                    <Typography sx={{ fontSize: 9.5, fontWeight: 800, color, textTransform: 'uppercase', letterSpacing: '.5px' }}>
                        {doc.type}
                    </Typography>
                    <Box sx={{ width: 3, height: 3, borderRadius: '50%', background: BRAND.line }} />
                    <Typography sx={{ fontSize: 10.5, color: BRAND.inkFaint }} noWrap>
                        {doc.categoryLabel}
                    </Typography>
                </Box>
            </Box>
        </Box>
    );
}

// ─── The detail pane ────────────────────────────────────────────────────────
function DetailPane({ doc, loading, onBack, showBack }) {
    if (loading) {
        return (
            <Box sx={{ display: 'grid', placeItems: 'center', height: '100%', gap: 1.5 }}>
                <CircularProgress size={26} sx={{ color: BRAND.navy }} />
                <Typography sx={{ fontSize: 13, color: BRAND.inkFaint }}>Loading document…</Typography>
            </Box>
        );
    }

    if (!doc) {
        return (
            <Box sx={{ display: 'grid', placeItems: 'center', height: '100%', px: 4, textAlign: 'center' }}>
                <Box>
                    <MenuBookIcon sx={{ fontSize: 52, color: BRAND.line, mb: 1.5 }} />
                    <Typography sx={{ fontSize: 15, fontWeight: 700, color: BRAND.inkMuted, mb: .75 }}>
                        Select a document
                    </Typography>
                    <Typography sx={{ fontSize: 13, color: BRAND.inkFaint, lineHeight: 1.7, maxWidth: 300 }}>
                        Choose a procedure or policy from the index to read its full steps.
                    </Typography>
                </Box>
            </Box>
        );
    }

    const color = TYPE_COLORS[doc.type] || BRAND.navy;
    const meta = [
        [VersionIcon,  'Version',     doc.version],
        [AuthorIcon,   'Author',      doc.author],
        [ApprovedIcon, 'Approved by', doc.approved_by],
        [ReviewIcon,   'Review date', doc.review_date],
    ].filter(([, , v]) => v);

    return (
        <Box sx={{ height: '100%', overflowY: 'auto', background: '#fff' }}>
            {/* document header */}
            <Box sx={{ px: { xs: 2.5, md: 4 }, pt: { xs: 2.5, md: 3.5 }, pb: 2.5, borderBottom: `1px solid ${BRAND.line}` }}>
                {showBack && (
                    <Button
                        onClick={onBack} startIcon={<BackIcon sx={{ fontSize: 13 }} />}
                        sx={{ mb: 1.5, ml: -1, textTransform: 'none', fontWeight: 700, fontSize: 13, color: BRAND.navy }}
                    >
                        All documents
                    </Button>
                )}

                <Box sx={{ display: 'flex', gap: .75, flexWrap: 'wrap', mb: 1.5 }}>
                    <Chip label={doc.type} size="small"
                        sx={{ height: 21, bgcolor: color, color: '#fff', fontWeight: 800, fontSize: 10.5 }} />
                    <Chip label={doc.status} size="small"
                        sx={{ height: 21, fontSize: 10.5, fontWeight: 700, bgcolor: `${STATUS_COLORS[doc.status] || BRAND.inkFaint}1F`, color: STATUS_COLORS[doc.status] || BRAND.inkFaint }} />
                    {doc.steps.length > 0 && (
                        <Chip
                            icon={<StepsIcon sx={{ fontSize: 12 }} />}
                            label={`${doc.steps.length} ${doc.type === 'SOP' ? 'steps' : 'sections'}`}
                            size="small"
                            sx={{ height: 21, fontSize: 10.5, fontWeight: 700, bgcolor: BRAND.canvas, color: BRAND.inkMuted, '& .MuiChip-icon': { color: BRAND.inkMuted, ml: .75 } }}
                        />
                    )}
                </Box>

                <Typography sx={{ fontSize: { xs: 18, md: 21 }, fontWeight: 800, color: BRAND.ink, lineHeight: 1.3, letterSpacing: '-.3px' }}>
                    {doc.title}
                </Typography>
                <Typography sx={{ fontSize: 12.5, color: BRAND.inkFaint, mt: .625 }}>
                    {doc.categoryLabel} &bull; {doc.department || 'All departments'}
                </Typography>

                {doc.description && (
                    <Typography sx={{ fontSize: 13.5, color: BRAND.inkMuted, lineHeight: 1.8, mt: 2 }}>
                        {doc.description}
                    </Typography>
                )}

                {doc.tags?.length > 0 && (
                    <Box sx={{ display: 'flex', gap: .75, flexWrap: 'wrap', mt: 2 }}>
                        {doc.tags.map(t => (
                            <Box key={t} sx={{ px: 1.125, py: .375, borderRadius: '7px', background: BRAND.canvas, border: `1px solid ${BRAND.line}` }}>
                                <Typography sx={{ fontSize: 10.5, fontWeight: 600, color: BRAND.inkMuted }}>#{t}</Typography>
                            </Box>
                        ))}
                    </Box>
                )}
            </Box>

            {/* steps — a vertical timeline, so the order is unmistakable */}
            <Box sx={{ px: { xs: 2.5, md: 4 }, py: 3 }}>
                <Typography sx={{ fontSize: 10.5, fontWeight: 800, color, textTransform: 'uppercase', letterSpacing: '1.1px', mb: 2.5 }}>
                    {doc.type === 'SOP' ? 'Procedure steps' : 'Policy sections'}
                </Typography>

                {doc.steps.length === 0 ? (
                    <Typography sx={{ fontSize: 13, color: BRAND.inkFaint, fontStyle: 'italic' }}>
                        No steps have been recorded for this document.
                    </Typography>
                ) : (
                    <Box>
                        {doc.steps.map((step, i) => {
                            const last = i === doc.steps.length - 1;
                            return (
                                <Box key={i} sx={{ display: 'flex', gap: 2 }}>
                                    {/* rail */}
                                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', flexShrink: 0 }}>
                                        <Box
                                            sx={{
                                                width: 26, height: 26, borderRadius: '50%', flexShrink: 0,
                                                background: color, display: 'grid', placeItems: 'center',
                                                boxShadow: `0 2px 8px ${color}4D`,
                                            }}
                                        >
                                            <Typography sx={{ fontSize: 11.5, fontWeight: 800, color: '#fff' }}>{i + 1}</Typography>
                                        </Box>
                                        {!last && <Box sx={{ width: 2, flex: 1, background: `${color}2E`, my: .75, borderRadius: 1 }} />}
                                    </Box>

                                    {/* body */}
                                    <Box sx={{ pb: last ? 0 : 3, minWidth: 0, flex: 1 }}>
                                        <Typography sx={{ fontSize: 14, fontWeight: 700, color: BRAND.ink, lineHeight: 1.4, mt: .25 }}>
                                            {step.label}
                                        </Typography>
                                        <Typography sx={{ fontSize: 13.5, color: BRAND.inkMuted, lineHeight: 1.85, mt: .75 }}>
                                            {step.content}
                                        </Typography>
                                    </Box>
                                </Box>
                            );
                        })}
                    </Box>
                )}
            </Box>

            {/* provenance */}
            {meta.length > 0 && (
                <>
                    <Divider />
                    <Box sx={{ px: { xs: 2.5, md: 4 }, py: 2.5, display: 'flex', gap: 3.5, flexWrap: 'wrap', background: BRAND.canvas }}>
                        {meta.map(([Icon, label, value]) => (
                            <Box key={label} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Icon sx={{ fontSize: 15, color: BRAND.inkFaint }} />
                                <Box>
                                    <Typography sx={{ fontSize: 9.5, color: BRAND.inkFaint, textTransform: 'uppercase', letterSpacing: '.6px' }}>
                                        {label}
                                    </Typography>
                                    <Typography sx={{ fontSize: 12, fontWeight: 700, color: BRAND.ink }}>{value}</Typography>
                                </Box>
                            </Box>
                        ))}
                    </Box>
                </>
            )}
        </Box>
    );
}

// ═════════════════════════════════════════════════════════════════════════════
function SopLibraryDialog({ open, onClose }) {
    const theme = useTheme();
    const isWide = useMediaQuery(theme.breakpoints.up('md'), { noSsr: true });

    const [docs, setDocs]         = useState([]);
    const [listLoading, setListLoading] = useState(false);
    const [listError, setListError]     = useState('');
    const [search, setSearch]     = useState('');
    const [filter, setFilter]     = useState('All');
    const [activeId, setActiveId] = useState(null);
    const [detail, setDetail]     = useState(null);
    const [detailLoading, setDetailLoading] = useState(false);

    // Full documents are fetched one at a time and kept, so flipping back to
    // one already read is instant instead of re-requesting it.
    const cache = useRef(new Map());

    // Whether the index has been requested. A REF, not state, and deliberately
    // so: an earlier version guarded on `listLoading` while also listing it as
    // a dependency, so calling setListLoading(true) re-ran the effect, whose
    // cleanup flagged the in-flight request as cancelled — the response was
    // then thrown away and the spinner never cleared. A ref changes no render,
    // so the effect stays settled while the request is in the air.
    const requested = useRef(false);

    // ── index ──────────────────────────────────────────────────────────────
    const loadIndex = useCallback(async () => {
        requested.current = true;
        setListLoading(true);
        setListError('');

        try {
            const { data } = await axios.get(loadAllSOPDocumentsdb);
            if (data?.success) {
                setDocs((data.data || []).map(normaliseDoc));
            } else {
                setListError(data?.message || 'The server did not return the document library.');
                requested.current = false;   // allow a retry
            }
        } catch (err) {
            console.error('SOP library load error:', err);
            setListError(
                err.response
                    ? `The server rejected the request (${err.response.status}).`
                    : 'Could not reach the server. Check your connection and try again.'
            );
            requested.current = false;       // allow a retry
        } finally {
            setListLoading(false);
        }
    }, []);

    useEffect(() => {
        if (open && !requested.current) loadIndex();
    }, [open, loadIndex]);

    // ── detail ─────────────────────────────────────────────────────────────
    const selectDoc = useCallback(async (id) => {
        setActiveId(id);

        if (cache.current.has(id)) {
            setDetail(cache.current.get(id));
            return;
        }

        setDetailLoading(true);
        setDetail(null);
        try {
            const { data } = await axios.get(loadSOPDocumentByIddb, { params: { id } });
            if (data.success) {
                const doc = normaliseDoc(data.data);
                cache.current.set(id, doc);
                setDetail(doc);
            }
        } catch (err) {
            console.error('SOP detail error:', err);
        } finally {
            setDetailLoading(false);
        }
    }, []);

    // On a wide screen the detail pane is always visible, so an empty one
    // looks broken — open the first document as soon as the index arrives.
    useEffect(() => {
        if (isWide && open && docs.length > 0 && activeId === null) {
            selectDoc(docs[0].id);
        }
    }, [isWide, open, docs, activeId, selectDoc]);

    const filtered = useMemo(() => {
        const s = search.trim().toLowerCase();
        return docs.filter(d =>
            (!s
                || d.title.toLowerCase().includes(s)
                || (d.categoryLabel || '').toLowerCase().includes(s)
                || (d.department || '').toLowerCase().includes(s))
            && (filter === 'All' || d.type === filter));
    }, [docs, search, filter]);

    const counts = useMemo(() => ({
        All:    docs.length,
        SOP:    docs.filter(d => d.type === 'SOP').length,
        Policy: docs.filter(d => d.type === 'Policy').length,
    }), [docs]);

    // On narrow screens the two panes share the space: index until something
    // is picked, then the document with a way back.
    const showIndex  = isWide || activeId === null;
    const showDetail = isWide || activeId !== null;

    return (
        <Dialog
            open={open} onClose={onClose} maxWidth="lg" fullWidth
            PaperProps={{
                sx: {
                    borderRadius: { xs: '14px', sm: '18px' }, overflow: 'hidden',
                    height: { xs: '94vh', sm: '88vh' }, m: { xs: 1, sm: 3 },
                    fontFamily: FONT_STACK,
                },
            }}
        >
            {/* ── header ── */}
            <Box sx={{ background: GRADIENT_NAVY, px: { xs: 2.5, md: 3.5 }, py: { xs: 2, md: 2.75 }, flexShrink: 0 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.75, minWidth: 0 }}>
                        <Box sx={{ width: 40, height: 40, borderRadius: '11px', background: 'rgba(255,255,255,.14)', border: '1px solid rgba(255,255,255,.2)', display: 'grid', placeItems: 'center', flexShrink: 0 }}>
                            <MenuBookIcon sx={{ fontSize: 20, color: '#fff' }} />
                        </Box>
                        <Box sx={{ minWidth: 0 }}>
                            <Typography sx={{ fontSize: { xs: 15, md: 17.5 }, fontWeight: 800, color: '#fff', lineHeight: 1.2 }}>
                                Policy &amp; SOP Library
                            </Typography>
                            <Typography sx={{ fontSize: 11.5, color: 'rgba(255,255,255,.55)' }}>
                                {docs.length} published document{docs.length === 1 ? '' : 's'} &bull; read-only
                            </Typography>
                        </Box>
                    </Box>
                    <IconButton size="small" onClick={onClose}
                        sx={{ color: '#fff', bgcolor: 'rgba(255,255,255,.14)', flexShrink: 0, '&:hover': { bgcolor: 'rgba(255,255,255,.26)' } }}>
                        <CloseIcon fontSize="small" />
                    </IconButton>
                </Box>
            </Box>

            {/* ── body ── */}
            <Box sx={{ flex: 1, minHeight: 0, display: 'grid', gridTemplateColumns: { xs: '1fr', md: '312px 1fr' } }}>

                {/* index */}
                {showIndex && (
                    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: 0, borderRight: { md: `1px solid ${BRAND.line}` }, background: BRAND.canvas }}>
                        <Box sx={{ p: 1.75, flexShrink: 0, borderBottom: `1px solid ${BRAND.line}` }}>
                            <TextField
                                size="small" fullWidth placeholder="Search procedures…"
                                value={search} onChange={e => setSearch(e.target.value)}
                                sx={{ '& .MuiOutlinedInput-root': { borderRadius: '10px', background: '#fff', fontSize: 13 } }}
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <SearchIcon sx={{ fontSize: 18, color: BRAND.inkFaint }} />
                                        </InputAdornment>
                                    ),
                                }}
                            />
                            <Box sx={{ display: 'flex', gap: .75, mt: 1.25 }}>
                                {['All', 'SOP', 'Policy'].map(t => {
                                    const on = filter === t;
                                    const c = TYPE_COLORS[t] || BRAND.navy;
                                    return (
                                        <Chip
                                            key={t} size="small" clickable onClick={() => setFilter(t)}
                                            label={`${t} ${counts[t] ?? 0}`}
                                            sx={{
                                                height: 26, fontSize: 11, fontWeight: 700, flex: 1,
                                                bgcolor: on ? c : '#fff',
                                                color:   on ? '#fff' : BRAND.inkMuted,
                                                border: `1px solid ${on ? 'transparent' : BRAND.line}`,
                                                '&:hover': { bgcolor: on ? c : BRAND.navySoft },
                                            }}
                                        />
                                    );
                                })}
                            </Box>
                        </Box>

                        <Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto', p: 1.25, display: 'flex', flexDirection: 'column', gap: .5 }}>
                            {listLoading ? (
                                <Box sx={{ display: 'grid', placeItems: 'center', py: 6, gap: 1.25 }}>
                                    <CircularProgress size={22} sx={{ color: BRAND.navy }} />
                                    <Typography sx={{ fontSize: 12.5, color: BRAND.inkFaint }}>Loading library…</Typography>
                                </Box>
                            ) : listError ? (
                                <Box sx={{ textAlign: 'center', py: 5, px: 2 }}>
                                    <CloudOffIcon sx={{ fontSize: 38, color: BRAND.line, mb: 1.25 }} />
                                    <Typography sx={{ fontSize: 12.5, color: BRAND.inkMuted, lineHeight: 1.7, mb: 2 }}>
                                        {listError}
                                    </Typography>
                                    <Button
                                        onClick={loadIndex} size="small" variant="outlined"
                                        startIcon={<RefreshIcon sx={{ fontSize: 15 }} />}
                                        sx={{ textTransform: 'none', fontWeight: 700, borderRadius: '9px', color: BRAND.navy, borderColor: BRAND.line, '&:hover': { borderColor: BRAND.navy, background: BRAND.navySoft } }}
                                    >
                                        Try again
                                    </Button>
                                </Box>
                            ) : filtered.length === 0 ? (
                                <Box sx={{ textAlign: 'center', py: 6, px: 2 }}>
                                    <MenuBookIcon sx={{ fontSize: 40, color: BRAND.line, mb: 1 }} />
                                    <Typography sx={{ fontSize: 12.5, color: BRAND.inkFaint }}>
                                        {docs.length === 0 ? 'No documents published yet.' : 'Nothing matches that search.'}
                                    </Typography>
                                </Box>
                            ) : (
                                filtered.map(doc => (
                                    <IndexRow key={doc.id} doc={doc} selected={doc.id === activeId} onSelect={selectDoc} />
                                ))
                            )}
                        </Box>
                    </Box>
                )}

                {/* detail */}
                {showDetail && (
                    <Box sx={{ minHeight: 0, minWidth: 0 }}>
                        <DetailPane
                            doc={detail} loading={detailLoading}
                            showBack={!isWide}
                            onBack={() => { setActiveId(null); setDetail(null); }}
                        />
                    </Box>
                )}
            </Box>
        </Dialog>
    );
}

export default SopLibraryDialog;
